import PadEditor from '../PadEditor';
// utils
import _ from 'lodash';
import { codeGeneretor } from 'app/utils';
// consts
import { TYPE } from 'app/config/prescription_content';
// form
import { prescriptionTypeInitFormFn } from 'app/components/modals/pad-editor/prescriptionModal/prescriptionForms/prescriptionTypeForm/utils/prescriptionTypeInitFormFn';
import { prescriptionDietInitFormFn } from 'app/components/modals/pad-editor/prescriptionModal/prescriptionForms/prescriptionDietForm/utils/prescriptionDietInitFormFn';
// classes
import { PadPrescriptionType } from './PadPrescriptionType';

/**
 * represents an instance of PadPrescriptionDiet
 * @class PadPrescriptionDiet
 * @extends PadEditor
 */
export default class PadPrescriptionDiet extends PadEditor {
  prepare(currentPadPrescriptionDiet = [], previousPadPrescriptionDiet = []) {
    const prepareAuxFn = padPrescription => {
      const id = padPrescription.id;
      const code = padPrescription.code || codeGeneretor();
      const type = TYPE.SUSTEINED;
      const status = padPrescription.status;
      const padPrescriptionSuggestSched = padPrescription.padPrescriptionSuggestionScheduling;

      padPrescription.suggestScheduling = _.isEmpty(padPrescriptionSuggestSched) ? 0 : 1;

      const prescriptionTypeForm = prescriptionTypeInitFormFn(padPrescription);
      const dietForm = prescriptionDietInitFormFn(padPrescription);

      return {
        id,
        code,
        type,
        status,
        prescriptionTypeForm,
        dietForm
      };
    };

    const preparedDiets = currentPadPrescriptionDiet.map(prepareAuxFn);
    const prePreparedDiets = previousPadPrescriptionDiet.map(prepareAuxFn);

    this._currentData = _.cloneDeep(preparedDiets);
    this._loadedData = _.cloneDeep(prePreparedDiets);
    this._screenData = _.cloneDeep(preparedDiets);

    return this;
  }

  /**
   * insert a new padPrescriptionDiet.
   * @param {object} padPrescriptionDiet - new padPrescriptionDiet.
   * @returns {object} the state
   */
  add(padPrescriptionDiet) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(padPrescriptionDiet);

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData, currentData });
    const type = padPrescriptionType.getTypeOnAdd();

    padPrescription.id = null;
    padPrescription.type = type;
    padPrescription.code = codeGeneretor();

    this._screenData.push(padPrescription);

    return this;
  }

  /**
   * update a padPrescriptionDiet.
   * @param {string} code - padPrescriptionDiet`s code to be edited.
   * @param {object} padPrescriptionDiet - padPrescriptionDiet to be edited.
   * @returns {object} return the state
   */
  edit(code, padPrescriptionDiet = {}) {
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(padPrescriptionDiet);

    // find index by code
    const indexScreenToEdit = [...this._screenData].findIndex(item => item.code === code);

    Object.entries(padPrescriptionDiet).forEach(([key, content]) => {
      this._screenData[indexScreenToEdit][key] = content;
    });

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData });
    const type = padPrescriptionType.getTypeOnEdit(padPrescription);

    this._screenData[indexScreenToEdit].type = type;

    return this;
  }

  /**
   * remove a element.
   * @param {string} code - element`s code to remove.
   * @returns {object} return the state
   */
  remove(code) {
    const newScreen = [...this._screenData].filter(currentElement => currentElement.code !== code);

    this._screenData = newScreen;

    return this;
  }

  /**
   * suspend a pad prescription padPrescriptionDiet.
   * @param {string} code - element`s code to suspend.
   * @returns {object} return the state
   */
  suspend(code) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData, currentData });

    const newScreen = [...this._screenData].map(cPadPrescriptionDiet => {
      // Nada é feito em pads prescrições que não corresponde ao que está sendo suspenso
      if (cPadPrescriptionDiet.code !== code) {
        return {
          ...cPadPrescriptionDiet
        };
      }

      // Nada deve ser feito se o pad prescrição já for suspenso. Esse reducer
      // nem deve ser acionado nesses caso, isso se trata de uma validação
      if (cPadPrescriptionDiet.type === TYPE.SUSPENDED) {
        return {
          ...cPadPrescriptionDiet
        };
      }

      const type = padPrescriptionType.getTypeOnSuspend(cPadPrescriptionDiet);

      return {
        ...cPadPrescriptionDiet,
        type
      };
    });

    this._screenData = newScreen;

    return this;
  }

  /**
   * unsuspend a pad prescription padPrescriptionDiet.
   * @param {string} code - element`s code to suspend.
   * @returns {object} return the state
   */
  unsuspend(code, prescriptionForm) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(prescriptionForm);

    const params = { screenData, loadedData, currentData };
    const padPrescriptionType = new PadPrescriptionType(params);

    const newScreen = [...this._screenData].map(cPadPrescriptionDiet => {
      if (cPadPrescriptionDiet.code !== code) {
        return {
          ...cPadPrescriptionDiet
        };
      }

      const padPrescriptionDiet =
        padPrescriptionType.findPadPrescriptionOnUnsuspend(padPrescription);

      return {
        ...padPrescriptionDiet
      };
    });

    this._screenData = newScreen;

    return this;
  }

  changeStatus(code) {
    this._screenData = [...this._screenData].map(padPrescriptionDiet => {
      if (padPrescriptionDiet.code === code) {
        const status = padPrescriptionDiet.status === 1 ? 0 : 1;

        return { ...padPrescriptionDiet, status };
      }

      return { ...padPrescriptionDiet };
    });

    return this;
  }

  replacesAllPrescriptors(prescriptionForm) {
    const { /* prescriptionTypeForm, */ dietForm } = prescriptionForm;
    const { formAddOn } = dietForm;
    const { professional } = formAddOn;

    const nScreen = [...this._screenData].map(prescription => {
      return {
        ...prescription,
        dietForm: {
          ...prescription.dietForm,
          alertMessage: {},

          professionalId: professional.id,
          responsibleForPrescription: {
            id: professional.id,
            name: professional.name,
            profession: professional.profession
          },
          formAddOn: {
            ...prescription.dietForm.formAddOn,
            professional: {
              ...professional
            }
          }
        }
      };
    });

    this._screenData = nScreen;
    this._state.data.screen = nScreen;

    return this;
  }
}
